import api from './api';

export const adminService = {
  /**
   * Fetch paginated users.
   * @param {object} params Query params like page, limit, role, search, etc.
   * @returns {Promise<{users: Array, pagination: object}>}
   */
  async getUsers(params = {}) {
    const response = await api.get('/admin/users', { params });
    return response.data.data;
  },

  /**
   * Fetch aggregated admin statistics.
   * @returns {Promise<object>}
   */
  async getStats() {
    const response = await api.get('/admin/stats');
    return response.data.data;
  },

  /**
   * Fetch the assignee and assignor matrix.
   * @returns {Promise<{assignors: Array, assignees: Array}>}
   */
  async getAssigneeMatrix() {
    const response = await api.get('/admin/assignee-matrix');
    return response.data.data;
  },

  /**
   * Create a new user.
   * @param {object} userData User data to create
   * @returns {Promise<object>}
   */
  async createUser(userData) {
    const response = await api.post('/admin/users', userData);
    return response.data.data;
  },

  /**
   * Export users in bulk (CSV or Excel).
   * API: GET /api/admin/users/export
   * 
   * @param {string} format Export format: 'csv' or 'excel' (default: 'csv')
   * @param {object} filters Optional filters:
   *   - role: string (admin, CH, MP, ED, GM)
   *   - isActive: boolean
   *   - region: string (case-insensitive partial match)
   *   - search: string (searches in name, email, designation)
   * @returns {Promise<Blob|object>} 
   *   - For CSV: Returns Blob for direct download
   *   - For Excel: Returns { users: Array, total: number, format: 'excel' }
   * 
   * @example
   * // Export all users as CSV
   * const blob = await adminService.exportUsers('csv');
   * 
   * // Export active users only as Excel
   * const data = await adminService.exportUsers('excel', { isActive: true });
   * 
   * // Export with filters
   * const blob = await adminService.exportUsers('csv', { role: 'GM', region: 'NR' });
   */
  async exportUsers(format = 'csv', filters = {}) {
    const params = { format, ...filters };
    
    if (format === 'csv') {
      // CSV returns blob with Content-Type: text/csv
      const response = await api.get('/admin/users/export', {
        params,
        responseType: 'blob',
      });
      return response.data;
    } else {
      // Excel returns JSON: { success: true, message: "...", data: { users: [...], total: number, format: "excel" } }
      const response = await api.get('/admin/users/export', { params });
      return response.data.data;
    }
  },

  /**
   * Import users in bulk from CSV/Excel file.
   * API: POST /api/admin/users/import
   * 
   * @param {File} file The file to upload (CSV or Excel, max 5MB)
   * @returns {Promise<object>} Returns import results:
   *   {
   *     success: boolean,
   *     message: string,
   *     data: {
   *       total: number,
   *       success: Array<{ row: number, email: string, name: string, id: string }>,
   *       errors: Array<{ row: number, email: string, error: string }>
   *     }
   *   }
   * 
   * @example
   * const result = await adminService.importUsers(file);
   * if (result.success) {
   *   console.log(`Imported ${result.data.success.length} users`);
   *   if (result.data.errors.length > 0) {
   *     console.warn(`${result.data.errors.length} errors occurred`);
   *   }
   * }
   * 
   * @throws {Error} On validation errors (400), server errors (500), or network errors
   */
  async importUsers(file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/admin/users/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    // API returns: { success: true, message: "...", data: { total, success: [...], errors: [...] } }
    return response.data;
  },
};


